(() => {
  if (customElements.get('bees-predictive-search')) return;

  class BeesPredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('#bees-predictive-results');
      this.abortController = null;
      this.debounceTimer = null;
      this.minimumCharacters = 2;

      if (!this.input || !this.results) return;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
    }

    onInput() {
      window.clearTimeout(this.debounceTimer);

      const query = this.input.value.trim();

      if (query.length < this.minimumCharacters) {
        this.close();
        return;
      }

      this.debounceTimer = window.setTimeout(() => {
        this.fetchResults(query);
      }, 180);
    }

    async fetchResults(query) {
      if (this.abortController) this.abortController.abort();

      this.abortController = new AbortController();

      const root = window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root
        : '/';

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const response = await fetch(
          `${root}search/suggest.json?${params.toString()}`,
          {
            signal: this.abortController.signal,
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!response.ok) throw new Error('Predictive search failed');

        const json = await response.json();
        this.renderResults(json.resources && json.resources.results, query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.renderError();
        }
      }
    }

    renderResults(results, query) {
      const products = results && results.products ? results.products : [];
      const collections = results && results.collections ? results.collections : [];
      const queries = results && results.queries ? results.queries : [];

      if (!products.length && !collections.length && !queries.length) {
        this.results.innerHTML = `
          <div class="bees-predictive-results__empty">
            No quick matches. Press enter to search for “${this.escapeHtml(query)}”.
          </div>
        `;
        this.open();
        return;
      }

      const productMarkup = products.length
        ? `
          <div class="bees-predictive-results__section">
            <p class="bees-predictive-results__heading">Products</p>
            ${products.map((product) => this.renderProduct(product)).join('')}
          </div>
        `
        : '';

      const collectionMarkup = collections.length
        ? `
          <div class="bees-predictive-results__section">
            <p class="bees-predictive-results__heading">Collections</p>
            ${collections.map((collection) => `
              <a class="bees-predictive-results__link" href="${collection.url}">
                ${this.escapeHtml(collection.title)}
              </a>
            `).join('')}
          </div>
        `
        : '';

      const queryMarkup = queries.length
        ? `
          <div class="bees-predictive-results__section">
            <p class="bees-predictive-results__heading">Suggested searches</p>
            ${queries.map((item) => `
              <a class="bees-predictive-results__link" href="${item.url}">
                ${this.escapeHtml(item.text || item.styled_text || '')}
              </a>
            `).join('')}
          </div>
        `
        : '';

      const viewAllMarkup = `
        <div class="bees-predictive-results__section">
          <a class="bees-predictive-results__link" href="/search?q=${encodeURIComponent(query)}">
            View all results for “${this.escapeHtml(query)}”
          </a>
        </div>
      `;

      this.results.innerHTML = productMarkup + collectionMarkup + queryMarkup + viewAllMarkup;
      this.open();
    }

    renderProduct(product) {
      const image = product.image
        ? `<img class="bees-predictive-results__image" src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy">`
        : `<span class="bees-predictive-results__image" aria-hidden="true"></span>`;

      const price = product.price
        ? `<span class="bees-predictive-results__price">${product.price}</span>`
        : '';

      return `
        <a class="bees-predictive-results__item" href="${product.url}">
          ${image}
          <span class="bees-predictive-results__title">${this.escapeHtml(product.title)}</span>
          ${price}
        </a>
      `;
    }

    renderError() {
      this.results.innerHTML = `
        <div class="bees-predictive-results__empty">
          Search suggestions are unavailable. Press enter to search.
        </div>
      `;
      this.open();
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define('bees-predictive-search', BeesPredictiveSearch);
})();